import { useMemo, useCallback } from 'react';
import { createLocalStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/local-storage/index.tsx';
import type { ProjectType } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { EXPERIMENT_KEY, type Banners } from '../../common/constants.tsx';

export type BannerDismissedStates = {
	month: number;
	'80_90': boolean;
	'90_100': boolean;
	gte100: boolean;
};

export type LocalStorage = {
	software: BannerDismissedStates;
	service_desk: BannerDismissedStates;
};

export const createUpsellAutomationLimitAwarenessLocalStorage = (prefix: string) =>
	createLocalStorageProvider(`${prefix}-${EXPERIMENT_KEY}`);

export const getCurrentMonth = () => new Date().getMonth();

export const bannerDismissedStatesDefault: LocalStorage = {
	software: {
		month: getCurrentMonth(),
		'80_90': false,
		'90_100': false,
		gte100: false,
	},
	service_desk: {
		month: getCurrentMonth(),
		'80_90': false,
		'90_100': false,
		gte100: false,
	},
};

export const getProductBannerDismissedStates = (
	projectType: ProjectType | undefined,
	cloudId: string,
) => {
	if (!cloudId || (projectType !== 'software' && projectType !== 'service_desk')) {
		return;
	}

	let dismissedStates: BannerDismissedStates | undefined;
	const upsellAutomationLimitAwarenessLocalStorage =
		createUpsellAutomationLimitAwarenessLocalStorage(cloudId);
	const localStorageData = upsellAutomationLimitAwarenessLocalStorage.get(EXPERIMENT_KEY);

	if (!localStorageData) {
		dismissedStates = bannerDismissedStatesDefault[projectType];
		upsellAutomationLimitAwarenessLocalStorage.set(EXPERIMENT_KEY, {
			...bannerDismissedStatesDefault,
		});
	} else {
		dismissedStates = localStorageData[projectType];
	}

	return dismissedStates;
};

type ShouldShowBannerProps = {
	bannerType: Banners | undefined;
	productBannerDismissedStates: BannerDismissedStates | undefined;
	currentMonth?: number;
};

export const shouldShowBanner = ({
	bannerType,
	productBannerDismissedStates,
	currentMonth = getCurrentMonth(),
}: ShouldShowBannerProps) => {
	if (!bannerType || !productBannerDismissedStates) {
		return false;
	}

	const stateIsNotDismissed =
		productBannerDismissedStates.month !== currentMonth ||
		productBannerDismissedStates[bannerType] === false;

	return stateIsNotDismissed;
};

export const useAutomationLimitBanner = (
	projectType: ProjectType | undefined,
	bannerType: Banners | undefined,
): {
	productBannerDismissedStates: BannerDismissedStates | undefined;
	showBanner: boolean;
	dismissBanner: (dismissProjectType: string, dismissBannerType: string) => void;
} => {
	const cloudId = useCloudId();

	const localStorageProvider = useMemo(() => {
		if (!cloudId) return null;
		return createUpsellAutomationLimitAwarenessLocalStorage(cloudId);
	}, [cloudId]);

	const currentMonth = useMemo(() => getCurrentMonth(), []);

	const productBannerDismissedStates = useMemo(() => {
		if (!localStorageProvider) {
			return undefined;
		}

		if (projectType !== 'software' && projectType !== 'service_desk') {
			return undefined;
		}

		const localStorageData = localStorageProvider.get(EXPERIMENT_KEY);

		if (!localStorageData) {
			const defaultStates = bannerDismissedStatesDefault[projectType];
			localStorageProvider.set(EXPERIMENT_KEY, {
				...bannerDismissedStatesDefault,
			});
			return defaultStates;
		}

		return localStorageData[projectType];
	}, [localStorageProvider, projectType]);

	const showBanner = useMemo(() => {
		return shouldShowBanner({
			bannerType,
			productBannerDismissedStates,
			currentMonth,
		});
	}, [bannerType, productBannerDismissedStates, currentMonth]);

	const dismissBannerCallback = useCallback(
		(dismissProjectType: string, dismissBannerType: string): void => {
			if (
				!localStorageProvider ||
				(dismissBannerType !== '80_90' &&
					dismissBannerType !== '90_100' &&
					dismissBannerType !== 'gte100')
			) {
				return;
			}

			const currentData = localStorageProvider.get(EXPERIMENT_KEY) || {
				...bannerDismissedStatesDefault,
			};

			currentData[dismissProjectType][dismissBannerType] = true;
			localStorageProvider.set(EXPERIMENT_KEY, currentData);
		},
		[localStorageProvider],
	);

	return {
		productBannerDismissedStates,
		showBanner,
		dismissBanner: dismissBannerCallback,
	};
};
